import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { prefersReducedMotion } from './useReducedMotion';

gsap.registerPlugin(ScrollTrigger);

/**
 * Global scroll-reveal system.
 *
 * Scans the document for elements with `data-reveal` attributes and
 * applies a buttery, GPU-friendly entrance as they enter the viewport.
 *
 * Modes (set as the value of `data-reveal`):
 *   - up       (default) y + blur + opacity
 *   - down     -y + blur + opacity
 *   - left     -x + opacity
 *   - right    +x + opacity
 *   - scale    scale-in + opacity
 *   - wipe     clip-path inset wipe from bottom
 *   - words    splits text into words, staggered y + opacity
 *
 * Optional attrs:
 *   - data-reveal-delay="0.2"
 *   - data-reveal-stagger="0.08"  (used by 'words' or as a child stagger if the
 *                                  element holds a list of children)
 *   - data-reveal-from="20%"      (override start position)
 *
 * Re-runs on route change so newly-mounted pages are captured.
 */
export default function useScrollReveal() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (prefersReducedMotion()) {
      document.querySelectorAll('[data-reveal]').forEach((el) => {
        el.style.opacity = '1';
        el.style.filter  = 'none';
        el.style.transform = 'none';
        el.style.clipPath = 'none';
      });
      return;
    }

    // Defer to next paint so React has rendered the new route
    const id = requestAnimationFrame(() => {
      const triggers = [];
      const els = document.querySelectorAll('[data-reveal]');

      els.forEach((el) => {
        if (el.dataset.revealReady === '1') return;
        el.dataset.revealReady = '1';

        const mode    = (el.dataset.reveal || 'up').toLowerCase();
        const delay   = parseFloat(el.dataset.revealDelay)   || 0;
        const stagger = parseFloat(el.dataset.revealStagger) || 0.08;

        const fromState = { opacity: 0 };
        const toState   = { opacity: 1, ease: 'power3.out', duration: 1.0, delay };

        if (mode === 'up') {
          fromState.y = 50; fromState.filter = 'blur(10px)';
          toState.y = 0;    toState.filter = 'blur(0px)';
        } else if (mode === 'down') {
          fromState.y = -50; fromState.filter = 'blur(10px)';
          toState.y = 0;     toState.filter = 'blur(0px)';
        } else if (mode === 'left') {
          fromState.x = -60;
          toState.x = 0;
        } else if (mode === 'right') {
          fromState.x = 60;
          toState.x = 0;
        } else if (mode === 'scale') {
          fromState.scale = 0.9; fromState.filter = 'blur(8px)';
          toState.scale = 1;     toState.filter = 'blur(0px)';
        } else if (mode === 'wipe') {
          fromState.clipPath = 'inset(0 0 100% 0)';
          fromState.y = 24;
          toState.clipPath = 'inset(0 0 0% 0)';
          toState.y = 0;
          toState.duration = 1.1;
        } else if (mode === 'words') {
          // Split text into word-spans (only once)
          if (!el.dataset.revealSplit) {
            const original = el.innerText;
            const words = original.split(/(\s+)/);
            el.innerHTML = '';
            words.forEach((w) => {
              if (/^\s+$/.test(w)) {
                el.appendChild(document.createTextNode(w));
              } else if (w) {
                const span = document.createElement('span');
                span.className = 'reveal-word';
                span.style.cssText = 'display:inline-block;will-change:transform,opacity;';
                span.textContent = w;
                el.appendChild(span);
              }
            });
            el.dataset.revealSplit = '1';
          }
          const wordEls = el.querySelectorAll('.reveal-word');
          gsap.set(wordEls, { y: 24, opacity: 0, filter: 'blur(8px)' });
          el.style.opacity = '1';
          const st = ScrollTrigger.create({
            trigger: el,
            start: 'top 88%',
            once: true,
            onEnter: () => {
              gsap.to(wordEls, {
                y: 0, opacity: 1, filter: 'blur(0px)',
                duration: 0.9, stagger, ease: 'power3.out', delay,
              });
            },
          });
          triggers.push(st);
          el.style.willChange = 'transform, filter';
          return;
        }

        gsap.set(el, fromState);
        el.style.willChange = 'transform, filter, clip-path';
        const st = ScrollTrigger.create({
          trigger: el,
          start: 'top 88%',
          once: true,
          onEnter: () => gsap.to(el, toState),
        });
        triggers.push(st);
      });

      // Force a refresh in case Lenis sized the document while we waited
      ScrollTrigger.refresh();

      // Stash so we can kill them on next route
      if (typeof window !== 'undefined') {
        window.__revealTriggers = (window.__revealTriggers || []).concat(triggers);
      }
    });

    return () => {
      cancelAnimationFrame(id);
      if (typeof window !== 'undefined' && window.__revealTriggers) {
        window.__revealTriggers.forEach((t) => t.kill && t.kill());
        window.__revealTriggers = [];
      }
      // Reset markers so a re-mount of the same page re-animates
      document.querySelectorAll('[data-reveal-ready]').forEach((el) => {
        delete el.dataset.revealReady;
      });
    };
  }, [pathname]);
}
